// Trade matching uses only the Standard browse projection. It infers relevant
// scope, never contract availability, and never reads contacts or Pro evidence.
export const TRADES = [
  { id: 'plumbing', label: 'Plumbing', aliases: ['plumber', 'plumbers', 'plumbing'] },
  { id: 'electrical', label: 'Electrical', aliases: ['electrician', 'electricians', 'electrical', 'sparky', 'sparkie'] },
  { id: 'building', label: 'Building', aliases: ['builder', 'builders', 'building', 'building contractor'] },
  { id: 'carpentry', label: 'Carpentry', aliases: ['carpenter', 'carpenters', 'carpentry', 'chippy'] },
  { id: 'roofing', label: 'Roofing', aliases: ['roofer', 'roofers', 'roofing'] },
  { id: 'concreting', label: 'Concreting', aliases: ['concreter', 'concreters', 'concreting', 'concrete contractor'] },
  { id: 'earthworks', label: 'Earthworks', aliases: ['earthworks', 'earthmoving', 'excavator', 'excavation', 'excavation contractor'] },
  { id: 'landscaping', label: 'Landscaping', aliases: ['landscaper', 'landscapers', 'landscaping', 'landscape gardener'] },
  { id: 'painting', label: 'Painting', aliases: ['painter', 'painters', 'painting'] },
  { id: 'air-conditioning', label: 'Air conditioning', aliases: ['air conditioning', 'air-conditioning', 'aircon', 'hvac', 'hvac contractor'] },
  { id: 'demolition', label: 'Demolition', aliases: ['demolition', 'demolition contractor', 'demolisher'] },
  { id: 'pools', label: 'Pool installation', aliases: ['pool', 'pools', 'pool builder', 'pool installer', 'pool installation'] },
];

export function resolveTrade(value) {
  const query = String(value || '').trim().toLowerCase();
  return TRADES.find(trade => trade.aliases.includes(query)) || null;
}

const CLOSED_STATUS = /rejected|refused|withdrawn|cancelled|canceled|expired|completed|lapsed|returned/i;
const PAST_WORK = /\b(retrospective|regulari[sz](?:ation|e)|as[- ]built|already (?:built|constructed)|existing use rights|use of (?:an? |the )?existing|building information certificate)\b/i;
const NEGATIVE_SCOPE = /\b(no|not|without|excluding|unchanged|retain(?:ed|ing)?(?!\s+walls?\b)|remov(?:e|al|ing)|demoli(?:sh|tion))\b/i;
const WORK_ACTION = /\b(new|proposed|construct(?:ion)?|erect(?:ion)?|install(?:ation)?|renovat(?:ion|e)|upgrad(?:e|ing)|replac(?:e|ement)|alterations?|additions?|extend|extension|re[- ]?roof(?:ing)?|repainting|landscaping)\b/i;
const DWELLING = '(?:dwelling(?:[- ]house)?s?|houses?|homes?(?!\\s+(?:office|studio|gym|theatre|business|based|occupation|extension|addition|renovation))|secondary dwelling|dual occupancy|residential units?|apartments?|townhouses?)';
const NEW_HOME = new RegExp(`\\b(?:new|construct(?:ion|ing)?|erect(?:ion|ing)?|proposed)\\s+(?:(?:of|a|an|the|one|two|three|four|single|double|storey|story|two-storey|single-storey|detached|attached|residential|secondary|[0-9]+)\\s+){0,6}${DWELLING}\\b`, 'i');
const DIRECT_SCOPE = {
  plumbing: [
    [/\b(plumbing|hot[- ]water|water supply|water reticulation)\b/i, 'Plumbing or water services in the scope'],
    [/\b(drainage|stormwater|sewer(?:age)?|sewage|septic|wastewater|on[- ]site effluent)\b/i, 'Drainage or wastewater works in the scope'],
    [/\b(bathrooms?|ensuites?|en[- ]suites?|toilets?|wet areas?)\b/i, 'Bathroom or wet-area works in the scope'],
    [/\b(kitchens?|laundr(?:y|ies))\b/i, 'Kitchen or laundry works in the scope'],
    [/\b(swimming pools?|plunge pools?)\b/i, 'Pool services may involve specialist plumbing'],
  ],
  electrical: [
    [/\b(electrical|rewir(?:e|ing)|switchboards?|power supply|lighting|solar panels?|photovoltaic|ev charg(?:er|ing))\b/i, 'Electrical, lighting or power works in the scope'],
  ],
  building: [
    [/\b(?:renovations?|extensions?|alterations?|additions?)\s+(?:(?:of|to|for)\s+)?(?:(?:a|an|the|approved|proposed|existing|new|single|two|storey|ground|first|floor|rear|side|front|internal|external)\s+){0,6}(?:dwellings?|houses?|homes?|buildings?|garages?|sheds?|kitchens?|bathrooms?|shops?|warehouses?|offices?|workshops?)\b(?!\s+(?:hours|opening|trading|operating|times?|use|business|conditions?|height|approval|consent))/i, 'Building construction or alterations in the scope'],
    [/\b(?:building|structural)\s+(?:renovations?|extensions?|alterations?|additions?)\b/i, 'Building construction or alterations in the scope'],
    [/\b(?:construct(?:ion)? of (?:a |an |the )?|new )(?:building|garage|shed)\b/i, 'Building construction or alterations in the scope'],
  ],
  carpentry: [
    [/\b(carpentry|timber fram(?:e|ing)|decks?|decking|pergolas?|verandahs?|verandas?|joinery|stairs?|staircases?)\b/i, 'Carpentry, deck or joinery works in the scope'],
  ],
  roofing: [
    [/\b(roof(?:ing|s)?|re[- ]?roof(?:ing)?|gutters?|guttering|downpipes?)\b/i, 'Roof or rainwater goods works in the scope'],
  ],
  concreting: [
    [/\b(concret(?:e|ing)|slabs?|footings?)\b/i, 'Concrete, slab or footing works in the scope'],
  ],
  earthworks: [
    [/\b(earthworks?|earthmoving|excavat(?:ion|e|ing)|cut and fill|site levell?ing)\b/i, 'Excavation or earthworks in the scope'],
  ],
  landscaping: [
    [/\b(landscap(?:e|ing)|garden construction|planting|turf(?:ing)?|paving)\b/i, 'Landscaping, planting or paving works in the scope'],
  ],
  painting: [
    [/\b(painting|repaint(?:ing)?|decorating)\b/i, 'Painting or decorating works in the scope'],
  ],
  'air-conditioning': [
    [/\b(air[- ]?conditioning|aircon|hvac|mechanical ventilation|ducted (?:heating|cooling)|space heating)\b/i, 'Air conditioning or ventilation works in the scope'],
  ],
  demolition: [
    [/\b(demolition|demolish(?:ing)?|removal of (?:(?:a|an|the|existing|old)\s+){0,3}(?:buildings?|dwellings?|houses?|sheds?|garages?|carports?|structures?|outbuildings?))\b/i, 'Demolition or building removal in the scope'],
  ],
  pools: [
    [/\b(swimming pools?|plunge pools?|pool installation)\b/i, 'Pool construction or installation in the scope'],
  ],
};
// Keep quantity limits ("not exceeding 1m") distinct from negated work.
const splitScope = source => source.split(/[,.;\n]|\b(?:and(?!\s+fill\b)|but|however)\b/i)
  .map(clause => clause.replace(/\b(?:not exceeding|no more than|not more than)\b/gi, 'maximum'));
const POOL_REMOVAL = /\b(?:infill(?:ing)?|filling in|decommission(?:ing|ed)?)\s+(?:of\s+)?(?:(?:the|a|an|existing)\s+){0,3}(?:swimming\s+|plunge\s+)?pools?\b/i;
const ADMIN_OBJECT = /\b(?:extensions?|alterations?|additions?|modifications?|changes?)\s+(?:of|to)\s+(?:(?:the|approved|existing|current|permitted|authorised|authorized|consented|statutory)\s+){0,4}(?:time|hours|consent|lapse|conditions?|approval|period|duration)\b/i;
const ADMIN_SCOPE = /\b(extension of (?:time|consent|lapse|approval)|(?:hours|days) of (?:use|operation|trade|trading)|(?:operating|trading|opening) hours|opening times?)\b/i;
const NEW_HOME_TRADES = new Set(['plumbing', 'electrical', 'building', 'carpentry', 'roofing', 'concreting', 'earthworks', 'painting']);

export function matchTradeOpportunity(row, tradeId) {
  const trade = TRADES.find(item => item.id === tradeId);
  if (!trade || !row || CLOSED_STATUS.test(row.status || '')) return null;
  const match = (basis, reason) => ({ basis, reason, tradeLabel: trade.label });
  const description = typeof row.description === 'string' ? row.description : '';
  const construction = typeof row.construction_type === 'string' ? row.construction_type : '';
  const development = typeof row.development_type === 'string' ? row.development_type : '';
  const source = description.trim() || `${construction} ${development}`;
  if (!source.trim() || PAST_WORK.test(source)) return null;

  // A proposal can include demolition followed by new work. Examine clauses so
  // the demolition does not become a match or suppress a separate new bathroom.
  const negativeScope = tradeId === 'demolition'
    ? /\b(no|not|without|excluding|unchanged|retain(?:ed|ing)?(?!\s+walls?\b))\b/i
    : NEGATIVE_SCOPE;
  let excludedContext = false;
  const clauses = splitScope(source).map(clause => {
    // Remove only barrier scope, preserving an independently stated new pool.
    let scope = clause.replace(/\b(?:swimming\s+)?pool\s+(?:safety\s+)?(?:fenc(?:e|ing)|barriers?)\b/gi, '');
    scope = scope.replace(/\b(?:fences?|fencing|(?:safety |child-resistant )?barriers?)\s+(?:around|for|to)\s+(?:(?:a|an|the|existing)\s+){0,3}(?:swimming |plunge )?pools?\b/gi, '');
    if (tradeId === 'carpentry') scope = scope.replace(/\bstair lifts?\b/gi, '');
    if (tradeId === 'roofing') scope = scope.replace(/\broof[- ]top terraces?\b/gi, '');
    return scope;
  }).filter(clause => {
    if (negativeScope.test(clause) || ((tradeId === 'plumbing' || tradeId === 'pools') && POOL_REMOVAL.test(clause))) {
      excludedContext = true;
      return false;
    }
    if (WORK_ACTION.test(clause) || (tradeId === 'demolition' && /\b(demolition|demolish(?:ing)?|removal)\b/i.test(clause))) excludedContext = false;
    else if (/\bexisting\b/i.test(clause)) excludedContext = true;
    if (excludedContext) return false;
    return !ADMIN_OBJECT.test(clause) && !ADMIN_SCOPE.test(clause) && !/\b(signage|sign|advertising|home[- ]based|home occupation)\b/i.test(clause);
  });
  for (const [pattern, reason] of DIRECT_SCOPE[tradeId]) {
    if (clauses.some(clause => pattern.test(clause))) return match('scope', reason);
  }
  // A generic housing classification plus 'new structure' does not establish
  // a new dwelling. Require stated dwelling construction for inference.
  if (!NEW_HOME_TRADES.has(tradeId)) return null;
  // A broad new-home inference cannot overrule an explicit exclusion for this trade.
  const excludedTrade = splitScope(source).some(clause =>
    /\b(no|not|without|excluding|unchanged|retain(?:ed|ing)?)\b/i.test(clause)
    && DIRECT_SCOPE[tradeId].some(([pattern]) => pattern.test(clause)));
  if (excludedTrade) return null;
  const newHomeReason = tradeId === 'building' ? 'New dwelling likely involves building work' : `New dwelling likely needs ${trade.label.toLowerCase()}`;
  if (clauses.some(clause => NEW_HOME.test(clause))) {
    return match('inferred', newHomeReason);
  }
  // Structured "New" construction can clarify a terse portal description, but
  // must not resurrect excluded/negated scope or a mere category classification.
  if (/^new(?: construction)?$/i.test(construction.trim()) && clauses.some(clause => new RegExp(`^\\s*${DWELLING}\\s*$`, 'i').test(clause))) {
    return match('inferred', newHomeReason);
  }
  return null;
}
